import { useMemo, useState } from 'react'
import { Field, ResultStat, Select, Slider } from './ui'

const CASE_GAP: Record<string, { label: string; gap: number }> = {
  B1: { label: 'Case B1 — Left Turn', gap: 7.5 },
  B2: { label: 'Case B2 — Right Turn', gap: 6.5 },
  B3: { label: 'Case B3 — Crossing', gap: 6.5 },
}

const CASE_OPTIONS = [
  { label: 'Case B1 — Left Turn', value: 'B1' as const },
  { label: 'Case B2 — Right Turn', value: 'B2' as const },
  { label: 'Case B3 — Crossing', value: 'B3' as const },
]

function AashtoSightDistance() {
  const [speed, setSpeed] = useState(45)
  const [grade, setGrade] = useState(0)
  const [maneuver, setManeuver] = useState('B1')

  const result = useMemo(() => {
    const base = CASE_GAP[maneuver]?.gap ?? 7.5
    const gradeAdj = grade > 3 ? 0.2 * (grade - 3) : 0
    const totalGap = base + gradeAdj
    const distanceFt = 1.47 * speed * totalGap
    const distanceM = distanceFt * 0.3048
    return { base, gradeAdj, totalGap, distanceFt, distanceM }
  }, [speed, grade, maneuver])

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <div className="space-y-5">
        <Field label="Design Speed of Crossing Highway" hint="15 – 80 mph">
          <Slider value={speed} onChange={setSpeed} min={15} max={80} unit=" mph" />
        </Field>
        <Field label="Minor Road Approach Grade" hint="-10% to +10%">
          <Slider value={grade} onChange={setGrade} min={-10} max={10} unit=" %" />
        </Field>
        <Field label="Intersection Maneuver Case">
          <Select value={maneuver} onChange={setManeuver} options={CASE_OPTIONS} />
        </Field>
      </div>

      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <ResultStat label="Sight Triangle Leg" value={`${result.distanceFt.toFixed(1)}`} sub="feet" accent="text-sky-600" />
          <ResultStat label="Metric" value={`${result.distanceM.toFixed(1)}`} sub="meters" />
        </div>
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 font-mono text-sm">
          <div className="flex justify-between py-1.5 text-slate-500">
            <span>Base time gap (t_g)</span>
            <span className="text-slate-700">{result.base.toFixed(1)} s</span>
          </div>
          <div className="flex justify-between py-1.5 text-slate-500">
            <span>Grade adjustment</span>
            <span className="text-slate-700">+{result.gradeAdj.toFixed(2)} s</span>
          </div>
          <div className="flex justify-between border-t border-slate-200 py-1.5 font-semibold text-slate-900">
            <span>Total time gap</span>
            <span>{result.totalGap.toFixed(2)} s</span>
          </div>
        </div>
        <p className="text-xs leading-relaxed text-slate-500">
          Formula: <span className="font-mono text-slate-700">D = 1.47 × V × T_total</span>. Grade penalty adds
          0.2 s per percent above 3% uphill. Reference: AASHTO Green Book, Intersection Sight Distance.
        </p>
      </div>
    </div>
  )
}

export default AashtoSightDistance
